import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { Upload, ArrowLeft, Loader2, Check } from 'lucide-react';

const Register = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();

    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;
    const freeLimit = settings?.free_uploads_limit || 1;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !email || !password) {
            toast.error('Please fill in all fields');
            return;
        }
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setLoading(true);
        try {
            await register(name, email, password);
            toast.success(`Account created! You have ${freeLimit} free upload${freeLimit > 1 ? 's' : ''}.`);
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        `${freeLimit} free background removal${freeLimit > 1 ? 's' : ''}`,
        "High-quality PNG exports",
        "AI-powered precision"
    ];

    return (
        <div className="min-h-screen bg-background flex">
            {/* Grain overlay */}
            <div className="grain-overlay" />
            
            {/* Left side - Brand */}
            <div className="hidden lg:flex lg:w-1/2 bg-foreground text-background p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="register-logo-link">
                    {logoUrl ? (
                        <img 
                            src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                            alt={businessName}
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Upload className="w-4 h-4 text-primary-foreground" />
                        </div>
                    )}
                    <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                </Link>
                
                <div>
                    <h2 className="font-heading font-bold text-4xl tracking-tight mb-4">
                        Start Creating
                    </h2>
                    <p className="text-background/70 text-lg mb-8">
                        Join thousands of creators using {businessName} for their projects.
                    </p>
                    <ul className="space-y-3">
                        {benefits.map((benefit, index) => (
                            <li key={index} className="flex items-center gap-3" data-testid={`register-benefit-${index}`}>
                                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                    <Check className="w-3 h-3 text-primary-foreground" />
                                </div>
                                <span className="text-background/80">{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="text-sm text-background/50">
                    © 2024 {businessName}. All rights reserved.
                </p>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link 
                        to="/" 
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                        data-testid="register-back-link"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to home
                    </Link>

                    <div className="lg:hidden mb-8">
                        <Link to="/" className="flex items-center gap-2" data-testid="register-mobile-logo-link">
                            {logoUrl ? (
                                <img 
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>
                    </div>

                    <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">
                        Create account
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Get started with your free image
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="name">Full Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="h-12"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-12"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Minimum 6 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-12"
                                data-testid="register-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full h-12 rounded-full text-base"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                'Create account'
                            )}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-8">
                        Already have an account?{' '}
                        <Link 
                            to="/login" 
                            className="text-primary font-medium hover:underline"
                            data-testid="register-login-link"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;
